import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { AlertTriangle, Check, Copy, Download, Laptop, RefreshCw, Smartphone } from '../../icons';
import {
  appDownloadFailure, safeDownloadUrl, appDownloadPageUrl, formatAppSize, getLatestAppRelease, requestAppReleaseDownload, shortFingerprint,
  type LatestReleaseResult,
} from '../../api/app-releases';
import { tDl } from '../../lib/app-download-i18n';
import './AppDownload.css';

/**
 * 설정 › 다운로드 — 사내 배포 앱 받기 (2026-09-14 · 시안 `axi-settings-download` v1 사용자 확정).
 *
 * - Android: 최신 게시본 카드(버전 · 빌드 · 게시일 · 크기 · 최소 지원 · 서명 지문 · 변경 내용) + QR(로그인 필요 주소) +
 *   「이 PC 로 받기」 · 「받기 주소 복사」 + 설치 안내 4단계.
 * - 기관 배포 방식이 MDM 이면 카드 대신 관리자 문의 안내(계약 `distribution.mode`).
 * - 게시본이 없거나 서버에 라우트가 없으면(404) 「아직 없음」.
 * - iPhone · iPad · Windows · macOS 는 「준비 중」.
 * ★ 종전 `DownloadPanel`(자리표시 셋)은 지우지 않는다 — 설정 모달이 이 패널을 쓰도록 한 줄만 바꾼다.
 */
type State =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: LatestReleaseResult };

/** QR 을 SVG 데이터 주소로 그린다 — 번들 포함 라이브러리(폐쇄망 · 외부 요청 없음). */
export function useQrDataUrl(text: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    QRCode.toString(text, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' })
      .then((svg) => { if (alive) setUrl(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`); })
      .catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; };
  }, [text]);
  return url;
}

function ymd(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function AppDownloadPanel() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const [downloadError, setDownloadError] = useState<'other' | 'revoked' | 'gone' | null>(null);
  const [busy, setBusy] = useState(false);

  // 첫 읽기만 캐시를 쓰고, 그 뒤(다시 시도 · 받기 실패 뒤)는 캐시를 건너뛴다.
  const load = useCallback((keepNotice = false, fresh = true) => {
    setState({ kind: 'loading' });
    if (!keepNotice) setDownloadError(null);
    (fresh ? getLatestAppRelease('android', { fresh: true }) : getLatestAppRelease('android'))
      .then((data) => setState({ kind: 'ready', data }))
      .catch(() => setState({ kind: 'error' }));
  }, []);
  useEffect(() => { load(false, false); }, [load]);

  const pageUrl = appDownloadPageUrl('android');
  const qr = useQrDataUrl(pageUrl);

  const onDownload = async (id: string) => {
    setBusy(true);
    setDownloadError(null);
    try {
      const res = await requestAppReleaseDownload(id);
      const url = safeDownloadUrl(res.downloadUrl);
      if (!url) { setDownloadError('other'); return; }
      window.location.assign(url);
    } catch (err) {
      const why = appDownloadFailure(err);
      if (why === 'revoked' || why === 'gone') { setDownloadError(why); load(true); }
      else if (why === 'mdm') { load(); }
      else setDownloadError('other');
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied('ok');
    } catch {
      setCopied('fail');
    }
  };

  const release = state.kind === 'ready' ? state.data.release : null;
  const mdm = state.kind === 'ready' && state.data.distribution.mode === 'MDM';

  return (
    <section className="settings-section appdl" aria-labelledby="st-appdl-h2">
      <h2 id="st-appdl-h2">{tDl('dl.heading')}</h2>
      <p className="settings-section-subtitle">{tDl('dl.desc')}</p>

      {(downloadError === 'revoked' || downloadError === 'gone') && <p className="appdl-alert" role="alert"><AlertTriangle size={16} aria-hidden />{tDl(downloadError === 'gone' ? 'dl.gone' : 'dl.revoked')}</p>}
      {state.kind === 'loading' && <p className="appdl-status" role="status">{tDl('dl.loading')}</p>}

      {state.kind === 'error' && (
        <div className="appdl-alert" role="alert">
          <AlertTriangle size={16} aria-hidden />
          <span>{tDl('dl.error')}</span>
          <button type="button" className="appdl-btn appdl-btn--line appdl-btn--sm" onClick={() => load()}>
            <RefreshCw size={14} aria-hidden />{tDl('dl.retry')}
          </button>
        </div>
      )}

      {state.kind === 'ready' && mdm && (
        <div className="appdl-card appdl-note" role="note" aria-label={tDl('dl.mdm_title')}>
          <span className="appdl-os" aria-hidden><Smartphone size={20} /></span>
          <div>
            <b>{tDl('dl.mdm_title')}</b>
            <span>{tDl('dl.mdm_desc')}</span>
            {state.data.distribution.mdmNote && <span className="appdl-mdmnote">{state.data.distribution.mdmNote}</span>}
          </div>
        </div>
      )}

      {state.kind === 'ready' && !mdm && !release && (
        <div className="appdl-card appdl-note" role="note" aria-label={tDl('dl.empty_title')}>
          <span className="appdl-os appdl-os--muted" aria-hidden><Smartphone size={20} /></span>
          <div><b>{tDl('dl.android')} — {tDl('dl.empty_title')}</b><span>{tDl('dl.empty_desc')}</span></div>
          <span className="appdl-badge appdl-badge--slate">{tDl('dl.pending')}</span>
        </div>
      )}

      {state.kind === 'ready' && !mdm && release && (
        <>
          <article className="appdl-card appdl-app" aria-label={tDl('dl.android')}>
            <div className="appdl-app__main">
              <div className="appdl-name">
                <span className="appdl-os" aria-hidden><Smartphone size={20} /></span>
                <div><h3>{tDl('dl.android')}</h3><small>AXI Works</small></div>
                <span className="appdl-badge appdl-badge--ok">{tDl('dl.latest')}</span>
                <span className="appdl-badge appdl-badge--pur">{release.versionName}</span>
              </div>
              <dl className="appdl-facts">
                <div><dt>{tDl('dl.fact_version')}</dt><dd className="num">{release.versionName} ({release.versionCode})</dd></div>
                <div><dt>{tDl('dl.fact_build')}</dt><dd className="num">{release.buildNumber ?? '—'}</dd></div>
                <div><dt>{tDl('dl.fact_published')}</dt><dd className="num">{ymd(release.publishedAt)}</dd></div>
                <div><dt>{tDl('dl.fact_size')}</dt><dd className="num">{formatAppSize(release.sizeBytes)}</dd></div>
                {release.minSupportedVersionCode !== null && (
                  <div><dt>{tDl('dl.fact_min')}</dt><dd className="num">{tDl('dl.fact_min_value', { code: release.minSupportedVersionCode })}</dd></div>
                )}
              </dl>
              {shortFingerprint(release.signingCertSha256) && (
                <p className="appdl-sig">
                  <Check size={14} aria-hidden />
                  <b>{tDl('dl.sig')}</b>
                  <span className="appdl-mono" title={release.signingCertSha256 ?? undefined}>{shortFingerprint(release.signingCertSha256)}</span>
                  <span>— {tDl('dl.sig_note')}</span>
                </p>
              )}
              {(release.releaseNotes ?? '').trim() && (
                <div className="appdl-notes">
                  <h4>{tDl('dl.notes')}</h4>
                  <ul>{(release.releaseNotes ?? '').split('\n').map((l) => l.replace(/^\s*[-*]\s*/, '').trim()).filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}</ul>
                </div>
              )}
              <div className="appdl-acts">
                <button type="button" className="appdl-btn" disabled={busy} onClick={() => { void onDownload(release.id); }}>
                  <Download size={16} aria-hidden />{tDl('dl.get_pc')}
                </button>
                <button type="button" className="appdl-btn appdl-btn--line" onClick={() => { void onCopy(); }}>
                  <Copy size={16} aria-hidden />{tDl('dl.copy')}
                </button>
                {copied === 'ok' && <span className="appdl-flash" role="status">{tDl('dl.copied')}</span>}
                {copied === 'fail' && <span className="appdl-flash appdl-flash--bad" role="status">{tDl('dl.copy_failed')}</span>}
              </div>
              {downloadError === 'other' && <p className="appdl-alert" role="alert"><AlertTriangle size={16} aria-hidden />{tDl('dl.download_failed')}</p>}
            </div>
            <div className="appdl-qr">
              {qr ? <img src={qr} alt={tDl('dl.qr_alt', { platform: tDl('dl.android') })} width={156} height={156} /> : <span className="appdl-qr__ph" aria-hidden />}
              <b>{tDl('dl.qr_title')}</b>
              <span>{tDl('dl.qr_desc')}</span>
              <span className="appdl-mono appdl-url">{pageUrl}</span>
            </div>
          </article>

          <section className="appdl-guide" aria-label={tDl('dl.guide_title')}>
            <h3>{tDl('dl.guide_title')}</h3>
            <ol>
              {([1, 2, 3, 4] as const).map((n) => (
                <li key={n}>
                  <span className="appdl-step" aria-hidden>{n}</span>
                  <b>{tDl(`dl.guide_${n}_t`)}</b>
                  <span>{tDl(`dl.guide_${n}_d`)}</span>
                </li>
              ))}
            </ol>
            <p className="appdl-warn"><AlertTriangle size={14} aria-hidden /><span>{tDl('dl.warn')}</span></p>
          </section>
        </>
      )}

      <div className="appdl-rows" aria-label="other-devices">
        <div className="appdl-row">
          <span className="appdl-os appdl-os--muted" aria-hidden><Smartphone size={16} /></span>
          <div><b>{tDl('dl.ios')}</b><span>{tDl('dl.ios_desc')}</span></div>
          <span className="appdl-badge appdl-badge--slate">{tDl('dl.pending')}</span>
        </div>
        <div className="appdl-row">
          <span className="appdl-os appdl-os--muted" aria-hidden><Laptop size={16} /></span>
          <div><b>{tDl('dl.pc')}</b><span>{tDl('dl.pc_desc')}</span></div>
          <span className="appdl-badge appdl-badge--slate">{tDl('dl.pending')}</span>
        </div>
      </div>
    </section>
  );
}
